import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ProductLineComponent } from '../product-line/product-line.component';
import { MapmodalDialogComponent } from '../../mapmodal-dialog/mapmodal-dialog.component';
import { CommonUtilityServiceService } from '../../services/common-utility-service.service';
import { UiSecretsService } from '../../services/ui-secrets.service';
import { SiteVm, ProductVm } from '../list-grouping.util';
import { createTestIdScope, buildTestId, slugify } from '../../shared/testid/testid.util';
import { SignedAtpSiteVisit } from '../../model/signed-atp-site-visit';
import {
  BulkEligibility, SiteDetail, BULK_SUBMIT_DISABLED_REASON, BULK_ACCEPT_DISABLED_REASON,
  BULK_ATP_DISABLED_REASON, BULK_DECLINE_DISABLED_REASON, ATP_IN_PROGRESS_REASON, siteScopeLabel,
} from '../bulk-eligibility.util';
import { ProjectActionLockService, PRODUCTS_LOCKED_REASON } from '../../services/project-action-lock.service';
import { DECLINE_ALL_TOO_MANY_REASON, tooManyToDecline } from '../batch-decline.util';

export type SiteBulkAction = 'submit' | 'accept' | 'atp' | 'decline';
export type SiteBulkSets = Record<SiteBulkAction, BulkEligibility | null>;

// CONNECT 2.0 list screens (Spec 4, Task 4) — one physical site: shared
// context header (map trigger, address, "Information Updated" stamp), the
// site-level facts row (acreage · total building sq ft · building count ·
// land use — 1.0 info-table parity), the Table A / special-site-condition
// callout, and a collapsible section holding the product lines.
//
// Field sourcing note (investigated for this task): 1.0's `info-table-item`
// only ever rendered acreage/land use in the grid — "total building sq ft"
// and "building count" were never info-table columns. Both fields DO exist
// on the site object though (see the richer `proposal-details` "Site Data"
// panel): `site.totalBuildingSqft` ("Square Footage") and `site.totalBuildings`
// ("Building Count"). Both are surfaced here to close the field-parity gap
// the spec calls out, with a "Not Provided" fallback (matching the existing
// land-use fallback) rather than 1.0's silent *ngIf hide.
//
// Collapse: implemented as a lightweight local toggle rather than reusing
// `info-header`. `info-header` is project-scoped (ATP buttons, project-overview
// link, submit-all-bids, project dates) and does not fit a per-site header, so
// a small self-contained accordion keeps the contract clean for Task 4.
@Component({
  standalone: true,
  selector: 'app-site-card',
  imports: [
    CommonModule,
    RouterLink,
    MatTooltipModule,
    ProductLineComponent,
    MapmodalDialogComponent,
  ],
  templateUrl: './site-card.component.html',
  styleUrl: './site-card.component.scss',
})
export class SiteCardComponent implements OnInit, OnChanges {
  // The site view-model (see list-grouping.util.ts). Required.
  @Input({ required: true }) site!: SiteVm;

  // Screen context: 'invitations' | 'proposals' | 'active'. Forwarded to each
  // product line and used here for the Active DocHub/comments additions.
  @Input() type: string = '';

  // Carried for product-line testids (Task 8) and deep-links (Task 6).
  @Input() projectId: string = '';

  // Optional project-scope segment for the site/product testids, threaded
  // page → project-group → here. The multi-project LIST screens
  // (invitations/proposals/active/project-history) set this to the project's
  // stable key so per-project site cards don't collide on a shared
  // `siteNumber` (every project has a site "#1", so a bare `site-1-...` id
  // repeats once per project group on those screens). Left empty on the
  // single-project surfaces (project-overview), where it drops cleanly out of
  // the id and the bare `site-<n>-...` format is preserved.
  @Input() testIdProjectScope: string = '';

  // The full, untouched raw project object, threaded from the page via
  // project-group so each product line can build its write/ATP payload
  // synchronously (no per-row async project lookup). Passed straight through.
  @Input() rawProject: any = null;

  // Task 3 (Project History): read-only mode, forwarded from project-group.
  // Swaps the Active "Open Workspace" button for a "View" link (same target
  // shape, driven off `type`) and suppresses every product line's action
  // area (see ProductLineComponent). Defaults false — every existing
  // consumer (Invitations/Pending/Active) is unaffected.
  @Input() readOnly: boolean = false;
  @Input() bulk: SiteBulkSets = { submit: null, accept: null, atp: null, decline: null };
  /** Whether a Sign All ATPs is already running on this project (`project-group` owns it). */
  @Input() atpInProgress = false;
  @Output() bulkAction = new EventEmitter<{ action: SiteBulkAction; rows: SiteDetail[] }>();

  readonly lockedReason = PRODUCTS_LOCKED_REASON;
  readonly declineTooManyReason = DECLINE_ALL_TOO_MANY_REASON;
  readonly bulkActions: { action: SiteBulkAction; label: string; suffix: string; disabledReason: string }[] = [
    { action: 'submit', label: 'Submit All Quotes', suffix: 'submit-all-quotes', disabledReason: BULK_SUBMIT_DISABLED_REASON },
    { action: 'accept', label: 'Accept All Offers', suffix: 'accept-all-offers', disabledReason: BULK_ACCEPT_DISABLED_REASON },
    { action: 'atp', label: 'Sign All ATPs', suffix: 'sign-all-atp', disabledReason: BULK_ATP_DISABLED_REASON },
    { action: 'decline', label: 'Decline All', suffix: 'decline-all', disabledReason: BULK_DECLINE_DISABLED_REASON },
  ];

  // Whether a write is still running on any of these products. Each site
  // button checks its own set, so a write elsewhere leaves it enabled.
  rowsLocked(rows: readonly SiteDetail[]): boolean {
    return rows.some((row) => this.lock.isLocked(row.proposal?.id));
  }

  /**
   * Why a site button is disabled, or null when it is enabled. The order is
   * the rule: a running write on one of its products, then (Sign All ATPs
   * only) an ATP already running on this project, then (Decline All only,
   * since the backend refuses more than 500 ids) the batch limit, then no
   * product ready for the action.
   */
  disabledReason(action: { action: SiteBulkAction; disabledReason: string }, rows: readonly SiteDetail[]): string | null {
    if (this.rowsLocked(rows)) {
      return this.lockedReason;
    }
    if (action.action === 'atp' && this.atpInProgress) {
      return ATP_IN_PROGRESS_REASON;
    }
    if (action.action === 'decline' && tooManyToDecline(rows)) {
      return this.declineTooManyReason;
    }
    if (!rows.length) {
      return action.disabledReason;
    }
    return null;
  }

  get hasBulkActions(): boolean {
    return !!(this.bulk.submit || this.bulk.accept || this.bulk.atp || this.bulk.decline);
  }

  get bulkScope(): string {
    return siteScopeLabel(this.site.siteNumber);
  }

  onBulkAction(action: SiteBulkAction): void {
    const set = this.bulk[action];
    const entry = this.bulkActions.find((candidate) => candidate.action === action);
    if (this.readOnly || !set || !entry || this.disabledReason(entry, set.eligible) !== null) {
      return;
    }
    this.bulkAction.emit({ action, rows: set.eligible });
  }

  // Pass-through of a product line's ATP-signature result, on its way to the
  // page hosting `atp-sign-accepted-dialog`. Nothing on this card reads it.
  @Output() atpSigned = new EventEmitter<SignedAtpSiteVisit[]>();

  expanded: boolean = true;
  mapModalActive: boolean = false;
  address: { streetAddress: string; cityStateZip: string } = {
    streetAddress: '',
    cityStateZip: '',
  };

  // Rollup label for a product's raw proposal status, used to build the
  // collapsed one-line summary (e.g. "2 products — 1 accepted, 1 quoted").
  // Kept separate from product-line's StatusPill map — this is prose, not a
  // pill key, and groups a couple of pill states together (e.g. all the
  // in-review states read as "in review" at a glance).
  private static readonly ROLLUP_LABELS: Record<string, string> = {
    INVITATION: 'invited',
    OFFERED: 'offered',
    COUNTERED: 'countered',
    RE_OFFERED: 're-offered',
    SUBMITTED: 'quoted',
    PROPOSAL: 'quoted',
    QUOTED: 'quoted',
    BID_WON: 'accepted',
    BID_LOST: 'not awarded',
    NO_RESPONSE: 'not awarded',
    ACTIVE: 'active',
    ADDRESSING_COMMENTS: 'addressing comments',
    REVIEW_PENDING: 'in review',
    REVISION_REQUESTED: 'in review',
    REVISION_PENDING: 'in review',
  };

  // Statuses that surface on the Active page — a site with any of these has
  // post-award work, so it gets the "Open Workspace" deep-link (on the Active
  // list AND on Project Overview, so the vendor finds it in one place).
  private static readonly ACTIVE_PHASE = new Set<string>([
    'ACTIVE',
    'REVIEW_PENDING',
    'REVISION_REQUESTED',
    'REVISION_PENDING',
  ]);

  constructor(
    private utilityService: CommonUtilityServiceService,
    private uiSecretsService: UiSecretsService,
    private lock: ProjectActionLockService
  ) {}

  ngOnInit(): void {
    // formatAddress reads `data.site.parcels`; wrap the representative site.
    this.address = this.utilityService.formatAddress({ site: this.site.site });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // The list refresh re-assigns `site` on this same component instance
    // (trackBy keeps the row alive), so recompute whenever it changes rather
    // than once in ngOnInit — otherwise productTestIds would go stale.
    // Recompute the product testids when either the site or the project scope
    // changes (both arrive together on first CD; the scope is stable per card
    // thereafter, but guard it so a re-scoped card never keeps stale ids).
    if (changes['site'] || changes['testIdProjectScope']) {
      this.productTestIds = this.computeProductTestIds();
    }
    if (changes['site']) {
      // Recompute the memoized map URL on the same site re-assignment (trackBy
      // keeps this instance alive across list refreshes), else a recycled card
      // keeps a stale thumbnail.
      this.staticMapUrl = this.computeStaticMapUrl();
    }
  }

  toggle(): void {
    this.expanded = !this.expanded;
  }

  toggleMapModal(): void {
    this.mapModalActive = !this.mapModalActive;
  }

  // ---------------------------------------------------------------------
  // Static map thumbnail (Product-requested). Same parcel-boundary sourcing
  // as the interactive `mapmodal-dialog` (opened by the same click),
  // rendered as a Google Static Maps URL instead of the JS Maps SDK — no
  // google.maps bootstrap needed just to show a preview.
  // ---------------------------------------------------------------------

  // Memoized once per `site` change (see ngOnChanges) rather than a getter: the
  // template reads this via `*ngIf="staticMapUrl as mapUrl"` on every
  // change-detection cycle, and recomputing it there (rebuild the
  // URLSearchParams + remap every parcel-boundary point) on each read — across
  // every card on a list — was a real per-navigation cost. Null until the
  // cached Maps API key (fetched at app init via UiSecretsService/
  // APP_INITIALIZER) and usable geometry exist; the template falls back to the
  // plain map icon while null, so a slow/failed key fetch or a parcel-less site
  // never renders a broken <img>.
  staticMapUrl: string | null = null;

  private computeStaticMapUrl(): string | null {
    const apiKey = this.uiSecretsService.getGoogleMapsApiKey();
    const parcels = this.site.site?.parcels;
    if (!apiKey || !parcels?.length) {
      return null;
    }

    const params = new URLSearchParams();
    params.set('maptype', 'hybrid');
    params.set('size', '150x104');
    params.set('scale', '2');

    const rings = this.parcelBoundaryRings;
    if (rings.length) {
      // Parcel geometry present — omit `center`/`zoom` entirely and let the
      // Static Maps API auto-fit the viewport to the `path` points (it pads
      // the frame automatically), instead of relying on the fixed
      // acreage-derived zoom, which was too tight for larger parcels and cut
      // the polygon off at the edge of the thumbnail.
      //
      // One `path` per parcel ring, closed back to its own first point —
      // matches the modal's polygon fill/stroke (#6296ff @ ~25%, #537ed5
      // weight 3).
      for (const ring of rings) {
        const closedRing = [...ring, ring[0]];
        const pathPoints = closedRing
          .map((point) => `${point.lat},${point.lng}`)
          .join('|');
        params.append(
          'path',
          `fillcolor:0x6296ff40|color:0x537ed5ff|weight:3|${pathPoints}`
        );
      }
    } else {
      // No usable parcel boundary — fall back to an explicit center + zoom
      // (auto-fit needs a path/markers spread to frame; a lone center point
      // has nothing to fit to) with a marker, matching connect-ws's zoom 16.
      const center = this.utilityService.determineMapCenter(parcels);
      params.set('center', `${center.lat},${center.lng}`);
      params.set('zoom', '16');
      params.set('markers', `color:0x537ed5|${center.lat},${center.lng}`);
    }

    params.set('key', apiKey);
    return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
  }

  // Parcel boundary rings, one per parcel — same source as the modal's
  // `detemineMapBounderies`, but only called once every parcel actually has
  // a non-empty `boundary` array. Guards the thumbnail from throwing on a
  // site with partial/malformed parcel data; falls back to the marker path
  // above instead.
  private get parcelBoundaryRings(): google.maps.LatLngLiteral[][] {
    const parcels = this.site.site?.parcels ?? [];
    const hasBoundaries = parcels.every(
      (parcel: any) =>
        Array.isArray(parcel?.boundary) && parcel.boundary.length > 0
    );
    if (!parcels.length || !hasBoundaries) {
      return [];
    }
    const boundaries = this.utilityService.detemineMapBounderies({
      site: this.site.site,
    }) as unknown as google.maps.LatLngLiteral[][];
    return (boundaries || []).filter(
      (ring) => Array.isArray(ring) && ring.length > 0
    );
  }

  get mapAltText(): string {
    const parts = [this.address.streetAddress, this.address.cityStateZip]
      .filter(Boolean);
    return `Map of ${parts.join(', ') || 'site'}`;
  }

  get productCount(): number {
    return this.site.products.length;
  }

  // trackBy for the product *ngFor — keep each product-line's DOM (and its
  // collapse/dialog state) across the list refreshes the action writes push,
  // instead of tearing every row down. Keyed on the stable proposal id, with
  // the site id / index as defensive fallbacks.
  trackByProduct(index: number, product: ProductVm): string {
    return (
      product?.proposal?.id ?? product?.siteDetail?.site?.id ?? `${index}`
    );
  }

  // Testid number/key segment: the raw site number (no zero-padding, matching
  // `product-line`'s own convention) when present. `list-grouping.util` falls
  // a siteNumber-less site back to grouping by `site.id` (its `key` is
  // `id:<site.id>`) — reuse that already-unique key here too, so two
  // siteNumber-less sites in the *same* project don't both collapse onto a
  // generic `site-x` base and collide.
  private get testidNum(): string {
    if (this.site.siteNumber != null) {
      return `${this.site.siteNumber}`;
    }
    return slugify(this.site.key) || 'x';
  }

  // The `project-<scope>` prefix parts, or `[]` when no scope is set. Prepended
  // to every site/product testid so `buildTestId` drops the segment entirely on
  // single-project surfaces (bare `site-<n>-...`) but keys per-project on the
  // multi-project list screens (`project-<slug>-site-<n>-...`). Slugification of
  // the scope itself happens inside `buildTestId`.
  private get projectScopeParts(): string[] {
    return this.testIdProjectScope ? ['project', this.testIdProjectScope] : [];
  }

  // Stable per-site testid root — `site-<n>` on single-project surfaces,
  // `project-<slug>-site-<n>` on the multi-project list screens.
  get testidBase(): string {
    return buildTestId(
      createTestIdScope(),
      ...this.projectScopeParts,
      'site',
      this.testidNum
    );
  }

  // Per-product `data-testid` bases, deduped within this site. Two products
  // at the same physical site can slugify to an identical title (e.g. both
  // lacking a subtype, or a repeated custom line item), which would otherwise
  // give every one of product-line's controls (status pill, submit/decline,
  // etc.) the same id — a Playwright strict-mode breaker. `createTestIdScope`
  // suffixes `-2`/`-3`... on collision, scoped to this one render pass, and
  // overrides product-line's own (collision-blind) fallback computation.
  // Computed once per `site` change (see ngOnChanges) rather than as a getter
  // — the template reads `productTestIds[i]` once per product per CD cycle,
  // and a getter would rebuild the scope + remap every product on every one
  // of those reads.
  productTestIds: string[] = [];

  private computeProductTestIds(): string[] {
    const scope = createTestIdScope();
    return this.site.products.map((product) =>
      buildTestId(
        scope,
        ...this.projectScopeParts,
        'site',
        this.testidNum,
        'product',
        product?.title || 'product'
      )
    );
  }

  // ---------------------------------------------------------------------
  // Facts row (site-level, 1.0 info-table parity) — "Not Provided" fallback
  // for any field genuinely absent from the source data.
  // ---------------------------------------------------------------------

  private static formatOrNotProvided(value: any, suffix?: string): string {
    if (value === null || value === undefined || value === '') {
      return 'Not Provided';
    }
    return suffix ? `${value} ${suffix}` : `${value}`;
  }

  get acreage(): string {
    return SiteCardComponent.formatOrNotProvided(
      this.site.site?.totalAcreage,
      'Acres'
    );
  }

  get totalBuildingSqft(): string {
    return SiteCardComponent.formatOrNotProvided(
      this.site.site?.totalBuildingSqft,
      'Sq. Ft.'
    );
  }

  get buildingCount(): string {
    return SiteCardComponent.formatOrNotProvided(this.site.site?.totalBuildings);
  }

  get landUse(): string {
    return SiteCardComponent.formatOrNotProvided(this.site.site?.landUse);
  }

  get updatedOn(): any {
    return this.site.site?.updatedOn ?? null;
  }

  // ---------------------------------------------------------------------
  // Table A changes (format preserved from the shipped `info-table-item`
  // +/- rendering) and special-site-condition — now two independent
  // callouts (info / danger), each with its own `hasX` gate.
  // ---------------------------------------------------------------------

  get specialSiteCondition(): string {
    return this.site.site?.specialSiteCondition ?? '';
  }

  get tableAItemsAdded(): string {
    return this.site.site?.tableAChanges?.itemsAdded ?? '';
  }

  get tableAItemsRemoved(): string {
    return this.site.site?.tableAChanges?.itemsRemoved ?? '';
  }

  get hasTableAChanges(): boolean {
    return !!(this.tableAItemsAdded || this.tableAItemsRemoved);
  }

  // ---------------------------------------------------------------------
  // Open Workspace deep-link (Active list + Project Overview).
  // ---------------------------------------------------------------------

  // True when the site has at least one product in an Active-page phase.
  get hasActiveProduct(): boolean {
    return this.site.products.some((product) =>
      SiteCardComponent.ACTIVE_PHASE.has(product.status)
    );
  }

  // site.id of the first Active-phase product. The workspace aggregates by
  // siteNumber and only shows Active-phase products, so this lands the deep
  // link on a genuine active product rather than a quote/awarded sibling.
  get openWorkspaceSiteId(): string | null {
    const active = this.site.products.find((product) =>
      SiteCardComponent.ACTIVE_PHASE.has(product.status)
    );
    return active?.site?.id ?? null;
  }

  // ---------------------------------------------------------------------
  // Active-only header additions.
  // ---------------------------------------------------------------------

  // No dedicated comments-count field exists anywhere in today's data model
  // (investigated: only a stub `comments-dialog` with no backing thread/count
  // — see Task 4 investigation notes in the handoff). Defensively reads a
  // conventional `comments` array off the site or any product's proposal so
  // this degrades to 0 (hidden) rather than assuming a shape that doesn't
  // exist yet. Task 6 wired the click target (below, to today's Active detail
  // route) but the count source itself is still the Task 4 placeholder — the
  // real Spec 5 comments source lands with the workspace rebuild.
  get commentsCount(): number {
    const siteComments = this.site.site?.comments?.length ?? 0;
    const proposalComments = this.site.products.reduce(
      (sum, product) => sum + (product.proposal?.comments?.length ?? 0),
      0
    );
    return siteComments + proposalComments;
  }

  // ---------------------------------------------------------------------
  // Collapsed one-line product roll-up, e.g. "2 products — 1 accepted, 1
  // quoted". Groups `site.products` by ROLLUP_LABELS, preserving first-seen
  // order.
  // ---------------------------------------------------------------------

  get rollupSummary(): string {
    const counts = new Map<string, number>();
    for (const product of this.site.products) {
      const label =
        SiteCardComponent.ROLLUP_LABELS[(product.status ?? '').toUpperCase()] ??
        'pending';
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    const breakdown = Array.from(counts.entries())
      .map(([label, count]) => `${count} ${label}`)
      .join(', ');
    const noun = this.productCount === 1 ? 'product' : 'products';
    return breakdown
      ? `${this.productCount} ${noun} — ${breakdown}`
      : `${this.productCount} ${noun}`;
  }
}
